import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil, firstValueFrom, filter, skip } from 'rxjs';
import { LIBRARY_TABS } from '../../../../models/enum';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LibraryItemHeaderComponent } from '../../../Common/library-item-header/library-item-header.component';
import { CommonModule } from '@angular/common';
import { CONSTANTS } from '../../../../Constants/Constants';
import { ReportsCommonService } from '../../../../services/reports/reports-common/reports-common.service';
import { ReportDispatcherService } from '../../../../services/reports/report-dispatcher/report-dispatcher.service';
import { reportItems } from '../../../../models/reports/ReportNameList';
import { IReportItem } from '../../../../models/reports/IReportItem';
import { IReportData } from '../../../../models/reports/IReportData';
import { ReportList } from '../../../../models/dtos/Enums/ReportList';
import { MatDialog } from '@angular/material/dialog';
import { DeleteReportDialogComponent } from '../../../dialogs/delete-report-dialog/delete-report-dialog.component';
import { DeleteReportDialogResult } from '../../../../models/reports/DeleteReportDialogResult';
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { CommonDataService } from '../../../../services/common-data.service';
import { MULTI_PDF_REPORT_KEYS } from '../../../../models/reports/MultiPdfReportKeys';


@Component({
  selector: 'app-report-view',
  imports: [MatCardModule,
    MatButtonModule,
    MatIconModule,
    LibraryItemHeaderComponent,
    TranslatePipe,
    CommonModule],
  templateUrl: './report-view.component.html',
  styleUrl: './report-view.component.scss'
})
export class ReportViewComponent implements OnInit, OnDestroy {
  @Output() unpinReportView = new EventEmitter<LIBRARY_TABS>();
  @Output() closeReportView = new EventEmitter<LIBRARY_TABS>();
  @Input() showContent: boolean = true;
  @Input()
  set isReviewTabVisible(value: boolean) {
    this._isReviewTabVisible = value;
    this.updateVisibleReportItems();
  }

  get isReviewTabVisible(): boolean {
    return this._isReviewTabVisible;
  }

  private readonly indentPerLevel = 24;
  indentPxByLevel: number[] = [];
  selectedReportUniqueId: string = '';
  selectedReport: string = '';
  destroy$: Subject<void> = new Subject<void>();
  reportItems: IReportItem[] = reportItems;
  visibleReportItems: IReportItem[] = reportItems;
  private _isReviewTabVisible: boolean = true;
  
  private isMultiPdfMode: boolean = false;
  private lastStandardReportUniqueId: string = '';
  private localLibraryItems: string[] = [];
  

  // TODO: To be replaced with actual data coming from DB
  private get multiPdfReportNames(): string[] {
    const staticCategories = [
      MULTI_PDF_REPORT_KEYS.MULTI_PDF,
      MULTI_PDF_REPORT_KEYS.USER_DEFINED,
      MULTI_PDF_REPORT_KEYS.STANDARD_LIBRARY,
      MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY
    ];
    
    // Dynamic Standard Library group names from API
    const standardLibraryNames = this.reportsCommonService.getStandardLibraryGroups().map(group => group.name);
    
    return [
      ...staticCategories,
      ...standardLibraryNames,
      ...this.localLibraryItems
    ];
  }
  
  constructor(
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly reportDispatcherService: ReportDispatcherService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly commonDataService: CommonDataService,
    private readonly dialog: MatDialog
  ) { }

  indentStyles: Array<{ [level: string]: number }> = [
    { 'padding-left.px': CONSTANTS.ZERO },
    { 'padding-left.px': this.indentPerLevel },
    { 'padding-left.px': this.indentPerLevel * CONSTANTS.TWO }
  ];


  ngOnInit(): void {
    if (this.reportItems.length > 0) {
      const savedName = this.reportsCommonService.getSelectedReportName();
      const savedItem = this.reportItems.find(r => r.reportName === savedName);
      this.selectedReportUniqueId = savedItem?.uniqueId ?? savedName;
      this.updateVisibleReportItems();
      this.setupReportCheckBoxSubscription();
      this.setupMaterialIndicatorSubscription();
      this.setupMultiPdfCancelledSubscription();
      this.setupMultiPdfGenerationSubscription();
      this.setupSaveReportGroupSubscription();
      this.setupPreferencesSavedSubscription();
      this.setupEstimateOpenedSubscription();
      this.setupProjectDataReadySubscription();
      this.setupEstimateClosedSubscription();
      this.setupOtherMetaDataChangedSubscription();
      this.loadMultiPdfTemplates(); 
      this.setupPreviousReportNameAfterCancelledSubscription();
    }
  }

  private setupOtherMetaDataChangedSubscription(): void {
    this.commonDataService.otherMetaDataChanged$
      .pipe(takeUntil(this.destroy$),skip(1))
      .subscribe(() => {
        if (this.isMultiPdfMode || this.reportsCommonService.isMultiPdfGenerating()
          || !this.isEstimateOpen() || !this.commonDataService.getProjectObjectDataAvailable()) {
          return;
        }
        this.selectReport();
      });
  }

  /**
   * Load multi-PDF templates from API and insert them into the report list
   */
  private loadMultiPdfTemplates(): void {
    if (!this.isEstimateOpen()) {
      return;
    }

    if (this.reportsCommonService.isMultiPdfTemplatesLoaded()) {
      this.insertMultiPdfItemsIntoList();
      return;
    }

    this.reportsCommonService.fetchMultiPdfTemplates();
    this.insertMultiPdfItemsIntoList();
  }

  /**
   * Insert fetched Standard Library and Local Library items into the reportItems list
   */
  private insertMultiPdfItemsIntoList(): void {
    // Insert Standard Library items after the "Standard Library" header
    const standardLibraryIndex = this.reportItems.findIndex(item => item.reportName === MULTI_PDF_REPORT_KEYS.STANDARD_LIBRARY);
    if (standardLibraryIndex !== CONSTANTS.NEGATIVE_ONE) {
      const standardGroups = this.reportsCommonService.getStandardLibraryGroups();
      const standardItems: IReportItem[] = standardGroups.map(group => ({
        level: CONSTANTS.TWO,
        reportName: group.name,
        id: CONSTANTS.ZERO as ReportList,
        uniqueId: group.id,
        isItLocal: false
      }));
      // Remove any existing level 2 items between Standard Library and Local Library
      const localLibraryIndex = this.reportItems.findIndex(item => item.reportName === MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY);
      const existingChildCount = localLibraryIndex !== CONSTANTS.NEGATIVE_ONE
        ? localLibraryIndex - standardLibraryIndex - CONSTANTS.ONE
        : CONSTANTS.ZERO;
      this.reportItems.splice(standardLibraryIndex + CONSTANTS.ONE, existingChildCount, ...standardItems);
    }

    // Insert Local Library items after the "Local Library" header
    const localLibraryIndex = this.reportItems.findIndex(item => item.reportName === MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY);
    if (localLibraryIndex !== CONSTANTS.NEGATIVE_ONE) {
      const localGroups = this.reportsCommonService.getLocalLibraryGroups();
      const localItems: IReportItem[] = localGroups.map(group => ({
        level: CONSTANTS.TWO,
        reportName: group.name,
        id: CONSTANTS.ZERO as ReportList,
        uniqueId: group.id,
        isItLocal: true
      }));
      // Remove any existing level 2 items after Local Library
      let existingChildCount = CONSTANTS.ZERO;
      for (let i = localLibraryIndex + CONSTANTS.ONE; i < this.reportItems.length; i++) {
        if (this.reportItems[i].level > CONSTANTS.ONE) {
          existingChildCount++;
        } else {
          break;
        }
      }
      this.reportItems.splice(localLibraryIndex + CONSTANTS.ONE, existingChildCount, ...localItems);
      // Update localLibraryItems for the multiPdfReportNames getter
      this.localLibraryItems = localGroups.map(g => g.name);
      this.updateVisibleReportItems();
    }

    const savedName = this.reportsCommonService.getSelectedReportName();
    const selectedItem = this.reportItems.find(item => item.reportName === savedName);
    if (selectedItem) {
      this.selectedReportUniqueId = selectedItem.uniqueId;
    }
  }

  private updateVisibleReportItems(): void {
    if (!this.isEstimateOpen()) {
      this.visibleReportItems = [];
      this.selectedReport = '';
      return;
    }

    this.visibleReportItems = this.reportsCommonService.getVisibleReportItems();

    if (this.selectedReport && !this.visibleReportItems.some(item => item.reportName === this.selectedReport)) {
      this.selectedReport = this.visibleReportItems[0]?.reportName ?? '';
      this.reportsCommonService.setSelectedReportName(this.selectedReport);
    }
  }

  /**
   * Subscribe to checkbox changes to regenerate reports
   */
  private setupReportCheckBoxSubscription(): void {
    this.reportsCommonService.reportCheckBoxChanged$
      .pipe(
        takeUntil(this.destroy$),
        skip(1)
      )
      .subscribe(() => {
        // Don't regenerate reports when in Multi-PDF mode
        if (this.isMultiPdfMode){
          return;
        }
        this.reportsCommonService.getGenericNameDictionaryByCultureId()
          .then(() => {
            this.selectReport();
          });
      });
  }

  /**
   * Subscribe to material indicator changes to regenerate the current report live
   */
  private setupMaterialIndicatorSubscription(): void {
    this.commonDataService.selectedMaterialIndicatorId$
      .pipe(
        filter((id): id is string => !!id),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isMultiPdfMode || !this.isEstimateOpen()) {
          return;
        }
        this.reportsCommonService.getGenericNameDictionaryByCultureId()
          .then(() => {
            this.selectReport();
          });
      });
  }
  
  /**
   * Subscribe to Multi-PDF cancelled event to reset mode and restore selection
   */
  private setupMultiPdfCancelledSubscription(): void {
    this.reportsCommonService.multiPdfCancelled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isMultiPdfMode = false;
        // Restore the last selected standard report and regenerate it
        if (this.lastStandardReportUniqueId) {
          this.selectedReportUniqueId = this.lastStandardReportUniqueId;
          const item = this.reportItems.find(r => r.uniqueId === this.lastStandardReportUniqueId);
          if (item) {
            this.reportsCommonService.setSelectedReportName(item.reportName);
            this.selectReport(item.reportName, item.uniqueId);
            return;
          }
        }

        const lastStandardReportName = this.reportsCommonService.getLastStandardReportNameBeforeMultiPdf();
        if (lastStandardReportName) {
          const itemByName = this.reportItems.find(r => r.reportName === lastStandardReportName);
          if (itemByName) {
            this.selectedReportUniqueId = itemByName.uniqueId;
            this.reportsCommonService.setSelectedReportName(itemByName.reportName);
            this.selectReport(itemByName.reportName, itemByName.uniqueId);
          }
        }
      });
  }
  
  /**
   * Subscribe to Multi-PDF generation requests
   */
  private setupMultiPdfGenerationSubscription(): void {
    this.reportsCommonService.generateMultiPdf$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reportKeys: string[]) => {
        this.generateMultiPdfReports(reportKeys);
      });
  }
  
  /**
   * Subscribe to estimate opened events to refresh the list and trigger the first report
   */
  private setupEstimateOpenedSubscription(): void {
    this.reportsCommonService.estimateOpened$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isMultiPdfMode = false;
        this.updateVisibleReportItems();
        this.loadMultiPdfTemplates();

        // Pre-select the target report so it's highlighted in the list
        const savedName = this.reportsCommonService.getSelectedReportName();
        const target = this.visibleReportItems.find(r => r.reportName === savedName)
          ?? this.visibleReportItems[0];
        if (target) {
          this.selectedReportUniqueId = target.uniqueId;
          this.reportsCommonService.setSelectedReportName(target.reportName);
        }
      });
  }

  /**
   * Trigger the initial report generation only once all project data is available.
   * Uses projectObjectDataAvailable$ which fires after level cost calculations complete,
   * ensuring all data (e.g. grossMargin) is populated before report services run.
   */
  private setupProjectDataReadySubscription(): void {
    this.commonDataService.projectObjectDataAvailable$
      .pipe(
        takeUntil(this.destroy$),
        filter(available => available)
      )
      .subscribe(() => {
        if (this.isMultiPdfMode || !this.isEstimateOpen()) return;
        this.reportsCommonService.getGenericNameDictionaryByCultureId()
          .then(() => this.selectReport());
      });
  }

  /**
   * Clear report list and selection when estimate is closed.
   */
  private setupEstimateClosedSubscription(): void {
    this.commonDataService.projectObjectDataAvailable$
      .pipe(
        takeUntil(this.destroy$),
        filter(available => !available)
      )
      .subscribe(() => {
        this.isMultiPdfMode = false;
        this.selectedReportUniqueId = '';
        this.selectedReport = '';
        this.updateVisibleReportItems();
      });
  }

  /**
   * Subscribe to preferences saved events to regenerate the current report
   */
  private setupPreferencesSavedSubscription(): void {
    this.reportsCommonService.preferencesSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMultiPdfMode) {
          return;
        }
        this.reportsCommonService.getGenericNameDictionaryByCultureId()
          .then(() => this.selectReport());
      });
  }

  /**
   * Subscribe to save report group events to update Local Library dynamically
   */
  private setupSaveReportGroupSubscription(): void {
    this.reportsCommonService.saveReportGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedGroup) => {
        this.addLocalLibraryItem(savedGroup.groupName);
        // Update selection to the newly saved local library item
        const newItem = this.reportItems.find(r => r.reportName === savedGroup.groupName && r.isItLocal);
        if (newItem) {
          this.selectedReportUniqueId = newItem.uniqueId;
          this.reportsCommonService.setSelectedReportName(savedGroup.groupName);
        }
      });
  }
  
  /**
   * Generate all reports for Multi-PDF sequentially
   */
  private async generateMultiPdfReports(reportKeys: string[]): Promise<void> {
    for (const reportKey of reportKeys) {
      // Set Multi-PDF generation mode with current report key
      this.reportsCommonService.startMultiPdfGeneration(reportKey);
      
      // Temporarily save the current selected report name
      const previousReportName = this.reportsCommonService.getSelectedReportName();
      
      // Generate the report - this will eventually call setSelectedReport
      // which now redirects to sendMultiPdfReportData when in Multi-PDF mode
      await this.generateReportForMultiPdf(reportKey);
      
      // Restore the previous report name
      this.reportsCommonService.setSelectedReportName(previousReportName);
    }
    
    // End Multi-PDF generation mode
    this.reportsCommonService.endMultiPdfGeneration();
  }
  
  /**
   * Generate a single report for Multi-PDF (returns a promise for sequencing)
   */
  private async generateReportForMultiPdf(reportName: string): Promise<void> {
    // Set the report name so generateReport picks it up
    this.reportsCommonService.setSelectedReportName(reportName);
    
    const reportDataPromise = firstValueFrom(
      this.reportsCommonService.multiPdfReportData$.pipe(
        filter((data) => data.reportKey === reportName),
        takeUntil(this.destroy$)
      )
    );
    
    // Trigger the report generation
    this.generateReportByName(reportName);
    
    // Wait for the report to complete
    await reportDataPromise;
  }

  unpinReport() {
    this.unpinReportView.emit(LIBRARY_TABS.ReportView);
  }

  closeReport() {
    this.closeReportView.emit(LIBRARY_TABS.ReportView);
  }


  /**
   * Check if the given report name is a Multi-PDF report
   */
  private isMultiPdfReport(reportName: string): boolean {
    return this.multiPdfReportNames.includes(reportName);
  }

  selectReport(reportName?: string, uniqueId?: string): void {
    if (this.isMultiPdfMode && reportName === MULTI_PDF_REPORT_KEYS.USER_DEFINED && this.selectedReportUniqueId === (uniqueId || reportName)) {
      return;
    }

    // In Multi-PDF mode, single-click on a standard report adds it to the selected list.
    if (this.isMultiPdfMode && reportName && !this.isMultiPdfReport(reportName)) {
      this.addToMultiPdfList(reportName);
      return;
    }
    
    // Save the last standard report selection BEFORE updating selectedReport
    // (so we can restore it when cancelling Multi-PDF mode)
    if (reportName && this.isMultiPdfReport(reportName) && !this.isMultiPdfMode) {
      if (this.selectedReportUniqueId) {
        const currentItem = this.reportItems.find(r => r.uniqueId === this.selectedReportUniqueId);
        if (currentItem && !this.isMultiPdfReport(currentItem.reportName)) {
          this.lastStandardReportUniqueId = this.selectedReportUniqueId;
          this.reportsCommonService.setLastStandardReportNameBeforeMultiPdf(currentItem.reportName);
        }
      }
    }
    
    if(reportName){
      this.reportsCommonService.setSelectedReportName(reportName);
      this.selectedReportUniqueId = uniqueId || reportName;

      if (!this.isMultiPdfReport(reportName)) {
        this.reportsCommonService.setLastStandardReportNameBeforeMultiPdf(reportName);
      }
    }

    let reportNameToGenerate = this.reportsCommonService.getSelectedReportName();

    // Check if this is a Multi-PDF report
    if (this.isMultiPdfReport(reportNameToGenerate)) {
      this.stopReportLoadingSpinner();
      this.handleMultiPdfReport(reportNameToGenerate, this.selectedReportUniqueId);
      return;
    }

    
    // Generate standard report using centralized configuration
    this.generateReportByName(reportNameToGenerate);
  }

  /**
   * Generate a report using the centralized dispatcher service
   * @param reportName - The name of the report to generate
   * @private
   */
  private generateReportByName(reportName: string): void {
    if (!this.commonDataService.getProjectObjectDataAvailable()) {
      this.stopReportLoadingSpinner();
      return;
    }

    const item = this.reportItems.find(r => r.reportName === reportName);
    if (item) {
      this.startReportLoadingSpinner();
      this.reportDispatcherService.generateReportByName(item.id);
      return;
    }

    this.stopReportLoadingSpinner();
  }

  private startReportLoadingSpinner(): void {
    if (this.reportsCommonService.isMultiPdfGenerating()) {
      return;
    }

    this.reportsCommonService.startReportLoading(this.translate.instant('REPORT_PROCESSING_MESSAGE'));
  }

  private stopReportLoadingSpinner(): void {
    this.reportsCommonService.stopReportLoading();
  }

  private isEstimateOpen(): boolean {
    const estimateId = this.commonDataService.getEstimateId();
    return estimateId !== CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  /**
    * Add a standard report to the Multi-PDF selected reports list (on single-click)
   * Only works when in Multi-PDF mode and only for non-Multi-PDF reports
   */
  addToMultiPdfList(reportName: string): void {
    // Only add to list if in Multi-PDF mode and it's NOT a Multi-PDF report
    if (this.isMultiPdfMode && !this.isMultiPdfReport(reportName)) {
      // Translate the report name before sending
      const translatedName = this.translate.instant(reportName);
      // Send both the original key and translated name
      this.reportsCommonService.addReportToMultiPdfList(reportName, translatedName);
    }
  }

  /**
   * Check if the given uniqueId belongs to a Local Library item
   * Uses the isLocal property derived from API's isItLocal
   */
  isLocalLibraryItem(uniqueId: string): boolean {
    const item = this.reportItems.find(r => r.uniqueId === uniqueId);
    return item?.isItLocal === true;
  }

  /**
   * Delete a Local Library group from the list
   * @param groupName - Name of the group to delete
   * @param event - Click event (to prevent propagation)
   */
  async deleteLocalLibraryGroup(groupName: string, event: Event): Promise<void> {
    event.stopPropagation(); // Prevent triggering selectReport
    
    // Show custom confirmation dialog
    const dialogRef = this.dialog.open(DeleteReportDialogComponent, {
      width: '400px',
      disableClose: true
    });
    
    const result: DeleteReportDialogResult = await firstValueFrom(dialogRef.afterClosed());
    
    if (!result?.confirmed) {
      return;
    }
    
    // Capture the group id before deletion (needed for selection check)
    const deletedItem = this.reportItems.find(item => item.reportName === groupName && item.isItLocal);
    const deletedUniqueId = deletedItem?.uniqueId;

    // Remove from the reportItems array
    const index = this.reportItems.findIndex(item => item.reportName === groupName && item.isItLocal);
    if (index !== CONSTANTS.NEGATIVE_ONE) {
      this.reportItems.splice(index, 1);
    }
    
    // Remove from localLibraryItems array (multiPdfReportNames getter will automatically update)
    const localLibraryIndex = this.localLibraryItems.indexOf(groupName);
    if (localLibraryIndex !== CONSTANTS.NEGATIVE_ONE) {
      this.localLibraryItems.splice(localLibraryIndex, 1);
    }
    
    // Delete from the service (API call)
    try {
      await this.reportsCommonService.deleteLocalLibraryGroup(groupName);
    } catch {
      // If API fails, re-add items back to the arrays
      if (index !== CONSTANTS.NEGATIVE_ONE) {
        this.reportItems.splice(index, 0, { reportName: groupName, level: 2 } as any);
      }
      if (localLibraryIndex !== CONSTANTS.NEGATIVE_ONE) {
        this.localLibraryItems.splice(localLibraryIndex, 0, groupName);
      }
      return;
    }
    
    // If the deleted item was selected, reset to last available report
    if (deletedUniqueId && this.selectedReportUniqueId === deletedUniqueId) {

      //clear the last multi-pdf state to avoid confusion when switching back to standard reports
      this.reportsCommonService.clearLastMultiPdfState();
      // Find remaining Local Library items after deletion
      const remainingLocalItems = this.reportItems.filter(r => r.isItLocal === true);

      if (remainingLocalItems.length > 0) {
        const lastLocalItem = remainingLocalItems[0]; // Select the first remaining Local Library item
        this.selectedReportUniqueId = lastLocalItem.uniqueId;
        this.reportsCommonService.setSelectedReportName(lastLocalItem.reportName);
        const translateFn = (key: string) => this.translate.instant(key);
        this.reportsCommonService.loadLocalLibraryGroup(lastLocalItem.reportName, translateFn);
      } else {
        this.isMultiPdfMode = false;
        if (this.lastStandardReportUniqueId) {
          this.selectedReportUniqueId = this.lastStandardReportUniqueId;
          const item = this.reportItems.find(r => r.uniqueId === this.lastStandardReportUniqueId);
          if (item) {
            this.reportsCommonService.setSelectedReportName(item.reportName);
            this.selectReport(item.reportName, item.uniqueId);
          }
        } else {
          const lastStandardName = this.reportsCommonService.getLastStandardReportNameBeforeMultiPdf();
          if (lastStandardName) {
            const item = this.reportItems.find(r => r.reportName === lastStandardName);
            if (item) {
              this.selectedReportUniqueId = item.uniqueId;
              this.reportsCommonService.setSelectedReportName(item.reportName);
              this.selectReport(item.reportName, item.uniqueId);
            }
          }
        }
      }
    }
    this.updateVisibleReportItems();
  }

  /**
   * Handle Multi-PDF report selection - sends isMultiPdf flag to iframe
   * and loads appropriate report group based on selection
   */
  private handleMultiPdfReport(reportName: string, uniqueId?: string): void {
    // Note: lastStandardReportSelection is now saved in selectReport() before we get here

    // Set Multi-PDF mode flag
    this.isMultiPdfMode = true;

    const translatedReportName = this.translate.instant(reportName);
    
    const multiPdfData: IReportData = {
      selectionName: translatedReportName,
      reportTitle: translatedReportName,
      reportTitles: { data: [] },
      tableData: [],
      internalReport: true,
      isMultiPdf: true
    };
    this.reportsCommonService.setSelectedReport(multiPdfData);
    
    // Handle different Multi-PDF modes
    const translateFn = (key: string) => this.translate.instant(key);
    
    // Use isLocal from IReportItem (derived from API's isItLocal property)
    // This prevents ambiguity when Standard and Local libraries have items with the same name
    const selectedItem = this.reportItems.find(r => r.uniqueId === uniqueId);
    if (selectedItem?.isItLocal === true) {
      this.reportsCommonService.loadLocalLibraryGroup(reportName, translateFn);
      return;
    }
    
    if (selectedItem?.isItLocal === false) {
      this.reportsCommonService.loadStandardLibraryGroup(reportName, translateFn);
      return;
    }
    
    // Handle User Defined (empty list)
    if (reportName === MULTI_PDF_REPORT_KEYS.USER_DEFINED) {
      this.reportsCommonService.loadUserDefinedMode(this.translate.instant(MULTI_PDF_REPORT_KEYS.USER_DEFINED));
      return;
    }
    
    // Handle "Multi-PDF" header - auto-select "User Defined"
    if (reportName === MULTI_PDF_REPORT_KEYS.MULTI_PDF) {
      this.selectedReportUniqueId = MULTI_PDF_REPORT_KEYS.USER_DEFINED;
      this.reportsCommonService.setSelectedReportName(MULTI_PDF_REPORT_KEYS.USER_DEFINED);
      this.reportsCommonService.loadUserDefinedMode(this.translate.instant(MULTI_PDF_REPORT_KEYS.USER_DEFINED));
      return;
    }
    
    // Handle "Standard Library" header - auto-select first child if available, otherwise select "User Defined"
    if (reportName === MULTI_PDF_REPORT_KEYS.STANDARD_LIBRARY) {
      const firstStandardLibraryItem = this.getFirstChildItem(MULTI_PDF_REPORT_KEYS.STANDARD_LIBRARY);
      if (firstStandardLibraryItem) {
        const firstItem = this.reportItems.find(r => r.reportName === firstStandardLibraryItem);
        this.selectedReportUniqueId = firstItem?.uniqueId ?? firstStandardLibraryItem;
        this.reportsCommonService.setSelectedReportName(firstStandardLibraryItem);
        this.reportsCommonService.loadStandardLibraryGroup(firstStandardLibraryItem, translateFn);
      } else {
        // No standard library items - default to User Defined
        this.selectedReportUniqueId = MULTI_PDF_REPORT_KEYS.USER_DEFINED;
        this.reportsCommonService.setSelectedReportName(MULTI_PDF_REPORT_KEYS.USER_DEFINED);
        this.reportsCommonService.loadUserDefinedMode(this.translate.instant(MULTI_PDF_REPORT_KEYS.USER_DEFINED));
      }
      return;
    }
    
    // Handle "Local Library" header - auto-select first child if available
    if (reportName === MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY) {
      const firstLocalLibraryItem = this.getFirstChildItem(MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY);
      if (firstLocalLibraryItem) {
        const firstItem = this.reportItems.find(r => r.reportName === firstLocalLibraryItem && r.isItLocal);
        this.selectedReportUniqueId = firstItem?.uniqueId ?? firstLocalLibraryItem;
        this.reportsCommonService.setSelectedReportName(firstLocalLibraryItem);
        this.reportsCommonService.loadLocalLibraryGroup(firstLocalLibraryItem, translateFn);
      } 
      else {
        // No local library items - default to last selected standard report 
        this.isMultiPdfMode = false;
        if(this.lastStandardReportUniqueId){
          this.selectedReportUniqueId = this.lastStandardReportUniqueId;
          const lastItem = this.reportItems.find(r => r.uniqueId === this.lastStandardReportUniqueId);
          if(lastItem){
            this.reportsCommonService.setSelectedReportName(lastItem.reportName);
            this.selectReport(lastItem.reportName, lastItem.uniqueId);
          }
        } else{
          const lastStandardReportName = this.reportsCommonService.getLastStandardReportNameBeforeMultiPdf();
          if(lastStandardReportName){
            const lastItem = this.reportItems.find(r => r.reportName === lastStandardReportName);
            if(lastItem){
              this.selectedReportUniqueId = lastItem.uniqueId;
              this.reportsCommonService.setSelectedReportName(lastItem.reportName);
              this.selectReport(lastItem.reportName, lastItem.uniqueId);
            }
          }
        }
      }
      return;
    }
  }
  
  /**
   * Dynamically add a Local Library item to the report list
   * @param groupName - Name of the newly saved Local Library group
   */
  private addLocalLibraryItem(groupName: string): void {
    // Guard against empty or whitespace-only names
    if (!groupName || !groupName.trim()) {
      return;
    }
    
    // Check if this group name already exists within the Local Library section only
    // (not the entire list - a Standard Library item can have the same name)
    const localLibraryIdx = this.reportItems.findIndex(item => item.reportName === MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY);
    if (localLibraryIdx !== CONSTANTS.NEGATIVE_ONE) {
      const localLibraryLevel = this.reportItems[localLibraryIdx].level;
      for (let i = localLibraryIdx + 1; i < this.reportItems.length; i++) {
        if (this.reportItems[i].level <= localLibraryLevel) {
          break; // Past Local Library children
        }
        if (this.reportItems[i].reportName === groupName) {
          return; // Already exists in Local Library, no need to add
        }
      }
    }
    
    // Add to localLibraryItems if not already present
    if (!this.localLibraryItems.includes(groupName)) {
      this.localLibraryItems.push(groupName);
    }
    
    // Find the index of "Local Library" to insert after it
    const localLibraryIndex = this.reportItems.findIndex(item => 
      item.reportName === MULTI_PDF_REPORT_KEYS.LOCAL_LIBRARY
    );
    
    if (localLibraryIndex !== -1) {
      // Find the last child of Local Library (items with level > Local Library's level)
      // to insert the new item at the end of the Local Library children
      const localLibraryLevel = this.reportItems[localLibraryIndex].level;
      let insertIndex = localLibraryIndex + 1;
      
      // Find the position after all existing Local Library children
      for (let i = localLibraryIndex + 1; i < this.reportItems.length; i++) {
        if (this.reportItems[i].level > localLibraryLevel) {
          insertIndex = i + 1; // Move insert position after this child
        } else {
          break; // Found an item at same or lower level, stop here
        }
      }
      
      // Use the API-generated id from the local library group as uniqueId
      const localGroup = this.reportsCommonService.getLocalLibraryGroups().find(g => g.name === groupName);
      const maxId = Math.max(...this.reportItems.map(item => item.id));
      this.reportItems.splice(insertIndex, 0, {
        level: 2,
        reportName: groupName,
        id: maxId + 1,
        uniqueId: localGroup?.id || groupName,
        isItLocal: true
      });
      this.updateVisibleReportItems();
    }
  }

  /**
   * Get the first child item after a parent header in the report list
   * @param parentName - The name of the parent header (e.g., 'Standard Library', 'Local Library')
   * @returns The first child item name, or null if no children exist
   */
  private getFirstChildItem(parentName: string): string | null {
    const parentIndex = this.reportItems.findIndex(item => item.reportName === parentName);
    
    if (parentIndex === -1) {
      return null; // Parent not found
    }
    
    const parentLevel = this.reportItems[parentIndex].level;
    
    // Look for the first item after the parent that has a higher level (child)
    for (let i = parentIndex + 1; i < this.reportItems.length; i++) {
      const item = this.reportItems[i];
      
      // If we encounter an item with same or lower level, we've passed all children
      if (item.level <= parentLevel) {
        break;
      }
      
      // Found a child item
      return item.reportName;
    }
    
    return null; // No children found
  }

  trackByUniqueId(index: number, item: IReportItem): string {
    return item.uniqueId;
  }

  // Subscribe to report switch cancellation events to restore previous selection
  private setupPreviousReportNameAfterCancelledSubscription() :  void{
    this.reportsCommonService.selectPreviousMultiPDF$
      .pipe(takeUntil(this.destroy$))
      .subscribe((previousGrpId : string) => {
        const item = this.reportItems.find(r => r.uniqueId === previousGrpId);
        if(item){
          this.selectedReportUniqueId = item.uniqueId;
          this.reportsCommonService.setSelectedReportName(item.reportName);
        }
      });
  }

  ngOnDestroy(): void {
    this.stopReportLoadingSpinner();
    this.reportsCommonService.pushMultiPdfReportData();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
